// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

import { Sparkles, X } from "lucide-react"
import React, { useEffect } from "react"

export interface ChangelogMessage {
  id: string
  content: React.ReactElement
}

interface ChangelogModalProps {
  message: ChangelogMessage
  onMarkAsSeen: () => void
  onClose: () => void
}

// US §22 — every close gesture (X, backdrop click, Esc) must dismiss the
// modal identically; the caller (`useChangelog`) is responsible for
// persisting that dismissal so it survives navigation.
export const ChangelogModal = ({
  message,
  onMarkAsSeen,
  onClose,
}: ChangelogModalProps) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    window.addEventListener("keydown", handleKeyDown)
    return () => window.removeEventListener("keydown", handleKeyDown)
  }, [onClose])

  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-[1000] p-12"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[540px] bg-[linear-gradient(180deg,#08242A,#051A1F)] border border-border-strong rounded-[20px] shadow-popover overflow-hidden animate-box-in"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-[22px] py-4">
          <span className="font-mono text-[10.5px] tracking-[0.14em] uppercase text-text-tertiary">
            What's New
          </span>
          <button
            onClick={onClose}
            title="Close"
            className="flex h-9 w-9 items-center justify-center rounded-[10px] border border-border text-text-tertiary cursor-pointer transition-colors hover:text-foreground hover:border-border-strong"
          >
            <X size={19} strokeWidth={2.1} />
          </button>
        </div>

        {/* Body */}
        <div className="flex flex-col items-center gap-4 px-10 pb-[26px] pt-8 text-center">
          <div className="flex h-[68px] w-[68px] items-center justify-center rounded-[20px] border border-primary/35 bg-primary/10 text-primary shadow-glow">
            <Sparkles size={30} strokeWidth={1.8} />
          </div>
          <span className="font-mono text-[11px] uppercase tracking-[0.1em] text-primary">
            New
          </span>

          <div className="w-full whitespace-pre-wrap select-text text-[13.5px] leading-relaxed text-text-secondary">
            {message.content}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-center border-t border-border px-[22px] py-5">
          <button
            onClick={onMarkAsSeen}
            className="rounded-[10px] bg-[linear-gradient(135deg,#45F0C8,#16C2A0)] px-5 py-2.5 text-sm font-semibold text-[#04141A] cursor-pointer transition-opacity hover:opacity-90"
            style={{ boxShadow: "0 0 18px rgba(69,240,200,0.3)" }}
          >
            Mark as Seen
          </button>
        </div>
      </div>
    </div>
  )
}
